/**
 * API helpers for Super Admin tenant management.
 * Proxies to /api/admin/tenants (Heimdall) with the given idToken.
 */

import type {
  TenantListResponse,
  TenantDTO,
  CreateTenantRequest,
  UpdateTenantRequest,
} from '@/shared/types';

export async function listTenants(
  idToken: string,
  skip: number = 0,
  limit: number = 100
): Promise<TenantListResponse> {
  const response = await fetch(
    `/api/admin/tenants?skip=${encodeURIComponent(String(skip))}&limit=${encodeURIComponent(String(limit))}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function getTenant(
    idToken: string,
    tenantId: string
): Promise<TenantDTO> {
  const response = await fetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function createTenant(
  idToken: string,
  body: CreateTenantRequest
): Promise<TenantDTO> {
  const response = await fetch('/api/admin/tenants', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function updateTenant(
    idToken: string,
    tenantId: string,
    body: UpdateTenantRequest
): Promise<TenantDTO> {
  const response = await fetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}
